import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Tabs,
  Tab,
  Container,
  IconButton,
  Divider,
  Grid,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import useNotification from "../Hooks/useNotification";
import useLoading from "../Hooks/useLoading";
import Loading from "../Alert/Loading";
import { fileInformation } from "../../api/archivalReportApi/archivalReportApi";
import LocalStorageUtil from "../../utils/localStorage";
interface CustomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tabLabels: string[];
  selectedDocument: any;
  businessUnitData: any;
}

const ArchivalReportDrawer: React.FC<CustomDrawerProps> = ({
  isOpen,
  onClose,
  tabLabels,
  selectedDocument,
  businessUnitData,
}) => {
  const [value, setValue] = useState<number>(0);
  const [generalInformation, setGeneralInformation] = useState<any>(null);
  const [versionHistory, setVersionHistory] = useState<any>(null);
  const { showFailure } = useNotification();
  const { loading, startLoading, stopLoading } = useLoading();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fetchInfoData = async (): Promise<any> => {
    startLoading();
    const payload = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      DocumentStreamId: selectedDocument?.documentStreamID,
      QueueTypeId: 2,
      UserId: LocalStorageUtil.getItem("userId"),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
      documentId: selectedDocument?.documentId,
      rendition: 11,
    };
    try {
      const response = await fileInformation(payload);
      setGeneralInformation(response?.generalInformation || []);
      setVersionHistory(response?.visionHistory || []);
    } catch (error) {
      showFailure("Something went wrong");
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInfoData();
    }
  }, [isOpen]);

  const renderGenralInformation = (
    generalInformation: Array<{
      groupName: string;
      group: Record<string, any>;
    }> | null
  ) => {
    if (!generalInformation || generalInformation.length === 0) {
      return <Typography variant="body1">No information available</Typography>;
    }

    return generalInformation.map((section) => (
      <Box key={section.groupName} sx={{ mt: 2 }}>
        {section.groupName && (
          <>
            <Typography variant="h6">{section.groupName}</Typography>
            <Divider component="div" role="presentation" /> {/* NOSONAR */}
          </>
        )}
        <Grid container spacing={2}>
          {Object.entries(section.group).map(([label, value]) => (
            <Grid item xs={12} key={label}>
              <Typography
                variant="subtitle2"
                color="#006ab6"
                style={{
                  fontFamily: "Verdana, Arial, Times New Roman, sans-serif",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                {value.fields}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {value.values ?? "-"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    ));
  };

  const renderVisionHistory = (
    visionHistory: Array<{
      level1: string;
      activityName: string;
      description: string;
      userName: string;
      activityDateTime: string;
    }> | null
  ) => {
    if (!visionHistory || visionHistory.length === 0) {
      return (
        <Typography variant="body1">No vision history available</Typography>
      );
    }

    return (
      <ul>
        {visionHistory.map((item) => (
          <li key={item.activityDateTime + item.userName}>
            <h6>
              <em>{item.level1}</em>&nbsp;
              <span>
                {item.activityName}:&nbsp;{item.description}
              </span>
              &nbsp;{item.userName}&nbsp;<span>On&nbsp;</span>
              <em>{new Date(item.activityDateTime).toLocaleString()}</em>
            </h6>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      <Loading show={loading} />
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        hideBackdrop={true}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          BackdropProps: {
            invisible: true,
          },
        }}
      >
        <Box sx={{ width: 300, padding: 2, position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6"></Typography>
            <IconButton onClick={onClose}>
              <ArrowForwardIcon />
            </IconButton>
          </Box>
          <Tabs
            value={value}
            onChange={handleTabChange}
            sx={{ mt: 2 }}
            TabIndicatorProps={{
              sx: {
                background: "blue",
              },
            }}
            style={{ textTransform: "capitalize" }}
          >
            {tabLabels.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
          <Container>
            {(() => {
              if (value === 0) {
                if (loading) {
                  return <Typography variant="body1">Loading...</Typography>;
                } else if (generalInformation) {
                  return renderGenralInformation(generalInformation);
                } else {
                  return "";
                }
              } else if (versionHistory) {
                return renderVisionHistory(versionHistory);
              } else {
                return "";
              }
            })()}
          </Container>
        </Box>
      </Drawer>
    </div>
  );
};

export default ArchivalReportDrawer;
